import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/core';

import AutosizeInput from 'react-input-autosize';
import * as yup from 'yup';
import NvIcon from 'shared/components/nv-icon';
import useClickOutside from 'shared/hooks/use-click-outside';
import NvPopover from './nv-popover';
import ValidationErrorMessage from './inputs/validation-error-message';

type NvPillWithInputProps = {
  value: string,
  onRemove: () => void
  onChange?: (value) => void
  disabled?: boolean
  onAdd?: () => void
  isAdded?: boolean
  className?: string
  placeholder?: string
  validationSchema?: yup.StringSchema,
  maxLength?: number,
  errorTitle?: string
  onError?: (error: string) => void
  dataQaRemove?: string
  dataQaInput?: string
  pendoTagRemove?: string
};

const NvPillWithInput = ({
  value,
  onRemove,
  onChange,
  disabled,
  onAdd,
  isAdded,
  className,
  placeholder,
  validationSchema,
  maxLength,
  errorTitle,
  onError,
  dataQaRemove,
  dataQaInput,
  pendoTagRemove,
}: NvPillWithInputProps) => {
  const [error, setError] = useState();
  const [newValue, setNewValue] = useState('');
  const inputRef = useRef();

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!!value.trim() && !error) {
        onAdd();
      } else {
        e.preventDefault();
      }
    }
  };

  const handleChange = (val) => {
    onChange?.(val);
    if (validationSchema) {
      validationSchema.validate(val).then(() => {
        setError(null);
        onError('');
      }).catch((err) => {
        setError(err?.message);
        onError(err?.message);
      });
    }
  };

  useClickOutside(inputRef, () => {
    if (!!value.trim() && !error) {
      onAdd?.();
    }
  });

  return (
    <NvPopover
      className='nv-text-input'
      placement='top'
      overlayStyles={css`
        max-width: 200px;
        .text-danger {
          font-weight: 600;
        }`}
      preventOverflow
      content={<ValidationErrorMessage text={error} title={errorTitle} />}
      show={!!error}
    >
      <div
        className={`d-flex align-items-center border ${className} ${error ? 'border-danger' : ''}`}
        ref={inputRef}
      >
        {!isAdded ? (
          <AutosizeInput
            maxLength={maxLength ?? 50}
            inputStyle={{ border: 'none', background: 'transparent', minWidth: 46, maxWidth: 210, outline: 'none', marginRight: 10 }}
            style={{ background: 'none', minWidth: 59, maxWidth: 220 }}
            name='form-field-name'
            autoFocus
            autoComplete='off'
            placeholder={placeholder}
            value={newValue}
            onChange={(event) => {
              setNewValue(event.target.value);
              handleChange(event.target.value);
            }}
            disabled={disabled}
            onKeyDown={(event) => onKeyDown(event)}
            data-qa={dataQaInput}
          />
        ) : (
          <span>
            {value}
          </span>
        )}
        <NvIcon
          icon='close'
          onClick={onRemove}
          size='xss-smallest'
          className='ml-2 gray-1'
          data-qa={dataQaRemove}
          pendo-tag-name={pendoTagRemove}
        />
      </div>
    </NvPopover>
  );
};

export default NvPillWithInput;
